import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { supabase } from '../lib/supabaseClient';
import { Button } from './ui/button'; // Assuming path is correct
import { Input } from './ui/input';   // Assuming path is correct

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(false); // Toggle between Sign Up and Sign In
  const [message, setMessage] = useState(''); // For user feedback
  const navigate = useNavigate(); // Get navigate function

  const handleAuth = async (event) => {
    event.preventDefault();
    setMessage(''); // Clear previous messages
    setLoading(true);

    try {
      let error;
      if (isSigningUp) {
        // Sign Up
        const { error: signUpError } = await supabase.auth.signUp({ email, password });
        error = signUpError;
        if (!error) setMessage('Check your email for the confirmation link!');
      } else {
        // Sign In
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        error = signInError;
        if (!error) {
          // Navigate to the app page on successful sign-in
          navigate('/app', { replace: true });
        }
      }

      if (error) throw error;

    } catch (error) {
      console.error('Error during authentication:', error);
      setMessage(`Error: ${error.error_description || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setMessage(''); // Clear previous messages
    setLoading(true); // Use existing loading state
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
      });
      if (error) throw error;
      // Supabase handles the redirect, no navigation needed here
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setMessage(`Error: ${error.error_description || error.message}`);
    } finally {
      // Don't set loading to false here, as the page will redirect
      // setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-black p-4 sm:p-8">
      <div className="p-6 bg-white/5 backdrop-blur-md rounded-xl shadow-2xl border border-white/10 w-full max-w-md">
        <h1 className="text-2xl font-bold mb-6 text-center text-white">
          {isSigningUp ? 'Create Account' : 'Sign In'}
        </h1>
        <form onSubmit={handleAuth}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">Email</label>
            <Input
              id="email"
              className="w-full bg-black/20 text-white border border-white/10 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 h-12"
              type="email"
              placeholder="your@email.com"
              value={email}
              required={true}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="mb-6">
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">Password</label>
            <Input
              id="password"
              className="w-full bg-black/20 text-white border border-white/10 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 h-12"
              type="password"
              placeholder="Your password"
              value={password}
              required={true}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <Button
            className="w-full mb-4 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white py-3 shadow-lg hover:shadow-xl font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (isSigningUp ? 'Sign Up' : 'Sign In')}
          </Button>
          {message && <p className={`text-center text-sm mb-4 ${message.startsWith('Error:') ? 'text-red-400' : 'text-green-400'}`}>{message}</p>}
        </form>

        {/* Google Sign In Button */}
        <Button
          variant="outline" // Use a different style if desired
          className="w-full mb-4 bg-white/10 hover:bg-white/20 text-white py-3 shadow-lg hover:shadow-xl font-semibold rounded-lg transition-all duration-300 border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          onClick={handleGoogleSignIn}
          disabled={loading}
          type="button" // Important: Prevent form submission
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            <>
              {/* Basic Google Icon SVG */}
              <svg className="h-5 w-5" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><title>Google</title><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/><path fill="none" d="M1 1h22v22H1z"/></svg>
              Sign in with Google
            </>
          )}
        </Button>

        <p className="text-center text-sm text-gray-300">
          {isSigningUp ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            onClick={() => {
              setIsSigningUp(!isSigningUp);
              setMessage(''); // Clear message on toggle
            }}
            className="font-medium text-blue-400 hover:text-blue-300"
            disabled={loading}
          >
            {isSigningUp ? 'Sign In' : 'Sign Up'}
          </button>
        </p>
      </div>
    </div>
  );
}
